
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Role, Permission, AdminUser } from '../types';
import { api } from '../utils/apiClient';
import { NAV_ITEMS } from '../constants';

interface RBACContextType {
  roles: Role[];
  currentUser: AdminUser | null;
  isLoading: boolean;
  isDemo: boolean;
  addRole: (role: Role) => void;
  updateRole: (id: string, updates: Partial<Role>) => void;
  deleteRole: (id: string) => void;
  hasPermission: (permission: Permission) => boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

const getCachedAuth = (): { user: AdminUser | null; roles: Role[] } => {
  try {
    const cachedUser = sessionStorage.getItem('es_cached_user');
    const cachedRoles = sessionStorage.getItem('es_cached_roles');
    return {
      user: cachedUser ? JSON.parse(cachedUser) : null,
      roles: cachedRoles ? JSON.parse(cachedRoles) : [],
    };
  } catch {
    return { user: null, roles: [] };
  }
};

const setCachedAuth = (user: AdminUser | null, roles: Role[]) => {
  try {
    if (user) {
      sessionStorage.setItem('es_cached_user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('es_cached_user');
    }
    if (roles.length > 0) {
      sessionStorage.setItem('es_cached_roles', JSON.stringify(roles));
    }
  } catch { /* sessionStorage unavailable */ }
};

export const RBACProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cached = getCachedAuth();
  const [roles, setRoles] = useState<Role[]>(cached.roles);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(cached.user);
  const [isLoading, setIsLoading] = useState(!cached.user);

  const fetchRoles = async () => {
    try {
      const { data, error } = await api.from('roles').select('*').order('name');
      if (error) throw error;
      if (data && data.length > 0) {
        setRoles(data);
        return data;
      }
      console.warn("No roles found in database");
      return [];
    } catch (err) {
      console.warn("Roles fetch failed:", err);
      return [];
    }
  };

  const fetchUserProfile = async () => {
    try {
      const { data, error } = await api.auth.getUser();
      if (error) throw error;

      if (data && data.user) {
        const u = data.user;
        const userObj: AdminUser = {
          id: u.id,
          name: u.name,
          email: u.email,
          roleId: u.role_id,
          roleName: u.role_name,
          status: u.status as 'Active' | 'Inactive',
          lastLogin: u.last_login,
          avatar: u.avatar || `https://ui-avatars.com/api/?name=${u.name}`
        };
        setCurrentUser(userObj);
        setCachedAuth(userObj, roles);
      }
    } catch (err) {
      console.error("Profile fetch failed:", err);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        const { data: { session } } = await api.auth.getSession();
        await fetchRoles();
        if (session) {
          await fetchUserProfile();
        }
      } catch (e) {
        console.warn("Auth initialization failed:", e);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Detect if the current user is a Demo Operator
  const isDemo = !!currentUser && (
    currentUser.roleName === 'Demo Operator' ||
    roles.find(r => r.id === currentUser.roleId)?.name === 'Demo Operator'
  );

  const hasPermission = (permission: Permission): boolean => {
    if (!currentUser) return false;
    const userRole = roles.find(r => r.id === currentUser.roleId || r.name === currentUser.roleName);
    
    // Safety check: if role not found yet, default to false but don't crash
    if (!userRole) return false;

    // Super Admins have all permissions
    if (userRole.name?.toLowerCase().includes('super admin')) return true;
    
    if (!userRole.permissions || !Array.isArray(userRole.permissions)) return false;

    // Demo Operators cannot access License, Roles, Cloud Storage, or Notification Log
    if (userRole.name === 'Demo Operator') {
      const DEMO_BLOCKED_PERMISSIONS = ['manage_license', 'manage_roles', 'view_logs'];
      if (DEMO_BLOCKED_PERMISSIONS.includes(permission)) return false;
    }

    // 1. Direct match (granular permission)
    if (userRole.permissions.includes(permission)) return true;

    // 2. Parent match (broad module-level permission)
    // Check if any top-level NAV_ITEM that the user has broad permission for contains this sub-permission
    const parentProvidesAccess = NAV_ITEMS.some(item => 
        item.permission && 
        userRole.permissions.includes(item.permission) && 
        item.submenu?.some(sub => sub.permission === permission)
    );

    return parentProvidesAccess;
  };



  const login = async (email: string, password: string) => {
    const { data, error } = await api.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message || error };

    // Refresh user profile and roles after login
    await fetchRoles();
    await fetchUserProfile();

    return { success: true };
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      // DEMO: If the user is a Demo Operator, reset all their changes before logging out
      const userRole = currentUser ? roles.find(r => r.id === currentUser.roleId || r.name === currentUser.roleName) : null;
      if (userRole?.name === 'Demo Operator') {
        try {
          console.log('[Demo] Resetting demo session data before logout...');
          const { data: sessionData } = await api.auth.getSession();
          const token = sessionData?.session?.access_token;
          const baseUrl = import.meta.env.VITE_API_URL || '/api';
          await fetch(`${baseUrl}/system/demo/reset`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          });
          console.log('[Demo] Session data reset successfully.');
        } catch (resetErr) {
          console.warn('[Demo] Could not reset demo session:', resetErr);
          // Continue with logout even if reset fails
        }
      }

      await api.auth.signOut();
      setCurrentUser(null);
      setCachedAuth(null, []);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    await fetchUserProfile();
  };

  const addRole = async (role: Role) => {
    try {
      const { data, error } = await api.from('roles').insert({
        id: role.id,
        name: role.name,
        description: role.description,
        permissions: role.permissions,
        is_system: role.isSystem
      });
      if (error) throw new Error(error);
      setRoles(prev => [...prev, role]);
    } catch (err) {
      console.error("Failed to add role:", err);
      alert(`Failed to save role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const updateRole = async (id: string, updates: Partial<Role>) => {
    try {
      const dbUpdates: any = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.permissions !== undefined) dbUpdates.permissions = updates.permissions;
      if (updates.isSystem !== undefined) dbUpdates.is_system = updates.isSystem;

      const { data, error } = await api.from('roles').update(dbUpdates).eq('id', id);
      if (error) throw new Error(error);
      setRoles(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
    } catch (err) {
      console.error("Failed to update role:", err);
      alert(`Failed to update role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const deleteRole = async (id: string) => {
    try {
      const { error } = await api.from('roles').delete().eq('id', id);
      if (error) throw new Error(error);
      setRoles(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error("Failed to delete role:", err);
      alert(`Failed to delete role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };


  return (
    <RBACContext.Provider value={{
      roles,
      currentUser,
      isLoading,
      isDemo,
      addRole,
      updateRole,
      deleteRole,
      hasPermission,
      login,
      logout,
      refreshUser
    }}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (context === undefined) throw new Error('useRBAC must be used within a RBACProvider');
  return context;
};


